/**
 * Canonical JSON, SHA-256 hashes and the finish-job fingerprint (test-plan §1).
 *
 * - manufacturingHash: SHA-256 over canonical JSON of the BuildModel's parts and
 *   openings (see build.ts `manufacturingPayload`). 64 hex characters.
 * - appearanceHash: SHA-256 over canonical JSON of design.appearance.
 * - The UI shows the first eight hex characters everywhere: `shortHash(h)`.
 */
import { sha256Hex } from './sha256';

/** JSON with object keys sorted recursively; arrays keep their order. Non-finite numbers become null (as JSON.stringify). */
export function canonicalJson(v: unknown): string {
  if (Array.isArray(v)) return '[' + v.map((x) => (x === undefined ? 'null' : canonicalJson(x))).join(',') + ']';
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    return (
      '{' +
      Object.keys(o)
        .filter((k) => o[k] !== undefined && typeof o[k] !== 'function')
        .sort()
        .map((k) => JSON.stringify(k) + ':' + canonicalJson(o[k]))
        .join(',') +
      '}'
    );
  }
  if (v === undefined) return 'null';
  return JSON.stringify(v);
}

/** SHA-256 (64 hex) of the canonical JSON of a value. */
export function hashOf(v: unknown): string {
  return sha256Hex(canonicalJson(v));
}

export const SHORT_HASH_LENGTH = 8;

/** Display form of a hash: the first eight hex characters. */
export function shortHash(hash: string | null | undefined): string {
  return (hash ?? '').slice(0, SHORT_HASH_LENGTH);
}

export interface FingerprintInput {
  manufacturingHash: string;
  appearanceHash: string;
  stockVersion: string;
  constructionVersion: string;
  appearanceProfileVersion: string;
  libraryVersion: string;
  pipelineVersion: string;
  qualityPreset: string;
}

/** Finish-job fingerprint: SHA-256 over the canonical JSON of exactly these eight fields. */
export function fingerprintOf(input: FingerprintInput): string {
  return hashOf({
    manufacturingHash: input.manufacturingHash,
    appearanceHash: input.appearanceHash,
    stockVersion: input.stockVersion,
    constructionVersion: input.constructionVersion,
    appearanceProfileVersion: input.appearanceProfileVersion,
    libraryVersion: input.libraryVersion,
    pipelineVersion: input.pipelineVersion,
    qualityPreset: input.qualityPreset,
  });
}
